import React, { useState, useEffect, useRef } from 'react'
import type { Flow, FlowPreferenceStatus } from '../../core/domain/models'
import { resolveFlowColor } from '../../core/domain/flowColors'
import { resolveFlowPreferenceStatus, cycleFlowPreferenceStatus } from '../../core/domain/flowPreferenceStatus'
import { StarFilledIcon, StarOutlineIcon, XCircleIcon } from './Icons'

export interface ItemFlowPreferencesProps {
  itemId: string
  flows: Flow[]
  /** Optional: highlight which flow is currently being viewed */
  selectedFlowId?: string
  /** Commit changes callback - called with itemId and a map of flowId → status */
  onCommit: (itemId: string, changes: Record<string, FlowPreferenceStatus>) => void
  /** When to commit: 'on-collapse' batches changes until the panel closes, 'immediate' commits each click */
  commitMode?: 'on-collapse' | 'immediate'
  /** Whether the panel is currently open (used for on-collapse commit mode) */
  isOpen?: boolean
}

/**
 * Reusable flow preference UI component.
 * Displays each flow with a 3-state cycle button (normal → preferred → notAvailable → normal).
 * Used in List view, ListBuilder, and Add Existing Items views.
 */
export function ItemFlowPreferences({
  itemId,
  flows,
  selectedFlowId,
  onCommit,
  commitMode = 'on-collapse',
  isOpen = true,
}: ItemFlowPreferencesProps) {
  // Track pending flow preference changes while in on-collapse mode
  const [pendingPreferences, setPendingPreferences] = useState<Record<string, FlowPreferenceStatus>>({})
  const prevOpenRef = useRef(isOpen)

  // When panel collapses, commit any pending preference changes
  useEffect(() => {
    if (commitMode !== 'on-collapse') return
    if (prevOpenRef.current && !isOpen) {
      if (Object.keys(pendingPreferences).length > 0) {
        onCommit(itemId, pendingPreferences)
        // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: reset after commit-on-collapse
        setPendingPreferences({})
      }
    }
    prevOpenRef.current = isOpen
  }, [isOpen, pendingPreferences, itemId, onCommit, commitMode])

  // Reset pending preferences when panel is re-opened
  useEffect(() => {
    if (isOpen) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- intentional: clear stale state on expand
      setPendingPreferences({})
    }
  }, [isOpen])

  // Compute effective flow status considering pending changes
  const getEffectiveStatus = (flowId: string): FlowPreferenceStatus => {
    if (pendingPreferences[flowId]) return pendingPreferences[flowId]
    const flow = flows.find(f => f.id === flowId)
    return resolveFlowPreferenceStatus(flow?.rankings[itemId])
  }

  const handleCyclePreference = (flowId: string) => {
    const currentStatus = getEffectiveStatus(flowId)
    const nextStatus = cycleFlowPreferenceStatus(currentStatus)

    if (commitMode === 'immediate') {
      onCommit(itemId, { [flowId]: nextStatus })
    } else {
      setPendingPreferences(prev => ({ ...prev, [flowId]: nextStatus }))
    }
  }

  // Check if item will be hidden (notAvailable on currently viewed flow)
  // Only show "will be hidden" note when the pending state changes to notAvailable
  const showHiddenNote = selectedFlowId
    ? pendingPreferences[selectedFlowId] === 'notAvailable'
    : false

  if (!flows || flows.length === 0) return null

  return (
    <div className="item-flows-list">
      {flows.map(f => {
        const effectiveStatus = getEffectiveStatus(f.id)
        const flowColor = resolveFlowColor(f)
        const isActiveFlow = f.id === selectedFlowId
        return (
          <div
            key={f.id}
            className={`item-flow-pref ${isActiveFlow ? 'viewing' : ''}`}
            style={{ '--switch-accent': `var(--flow-accent-${flowColor})` } as React.CSSProperties}
          >
            <button
              type="button"
              className="item-flow-pref-btn"
              onClick={(e) => {
                e.stopPropagation()
                handleCyclePreference(f.id)
              }}
              title={`${f.name}: ${effectiveStatus === 'normal' ? 'Available' : effectiveStatus === 'preferred' ? 'Preferred' : 'Not available'} (click to cycle)`}
              aria-label={`${f.name} preference: ${effectiveStatus}`}
            >
              {renderFlowStatusIcon(effectiveStatus)}
            </button>
            <span className="item-flow-pref-label">
              {f.name}
              {isActiveFlow && <span className="item-flow-switch-viewing">viewing</span>}
            </span>
          </div>
        )
      })}
      {showHiddenNote && (
        <div className="item-flow-hidden-note">
          item will be hidden in this flow
        </div>
      )}
    </div>
  )
}

function renderFlowStatusIcon(status: FlowPreferenceStatus) {
  switch (status) {
    case 'normal':
      return <span className="flow-pref-icon flow-pref-normal"><StarOutlineIcon size={16} /></span>
    case 'preferred':
      return <span className="flow-pref-icon flow-pref-preferred"><StarFilledIcon size={16} /></span>
    case 'notAvailable':
      return <span className="flow-pref-icon flow-pref-unavailable"><XCircleIcon size={16} /></span>
  }
}
